// アカウント操作（ログイン / マイページ / パスワード変更 / ログアウト）。
//
// アクセストークンの寿命が7日あるため、ログアウトの導線が無いと共用端末の
// セッションが1週間残る。また管理者が発行した仮パスワードを本人が置き換える
// 手段もここにしかない。予測3コンテンツ共通の PredictionServiceNav に載せる。
//
// **未ログインでも表示される**（#36 で3コンテンツを公開ルートにしたため）。
// その状態でログアウトやパスワード変更を出しても押せないので、ログインだけを見せる。
import React, { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { AuthContext } from '../../context/AuthContext';

const MIN_PASSWORD_LENGTH = 8; // サーバ側 schemas.PasswordChangeRequest と揃える

const BUTTON_SX = {
  textTransform: 'none',
  borderRadius: 2,
  px: 1.5,
  py: 0.5,
  fontSize: 12,
  color: 'rgba(203, 229, 245, 0.86)',
  border: '1px solid rgba(92, 160, 219, 0.28)',
};

function AccountMenu() {
  const { logout, changePassword, isAuthenticated } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const closeDialog = () => {
    setOpen(false);
    setForm({ current: '', next: '', confirm: '' });
    setError('');
    setDone(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (form.next !== form.confirm) {
      setError('新しいパスワードが一致しません');
      return;
    }
    if (form.next.length < MIN_PASSWORD_LENGTH) {
      setError(`新しいパスワードは${MIN_PASSWORD_LENGTH}文字以上にしてください`);
      return;
    }

    setBusy(true);
    setError('');
    try {
      await changePassword(form.current, form.next);
      setDone(true);
      setForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Stack direction="row" spacing={1}>
        <Button sx={BUTTON_SX} component={RouterLink} to="/login">
          ログイン
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button sx={BUTTON_SX} component={RouterLink} to="/mypage">
          マイページ
        </Button>
        <Button sx={BUTTON_SX} onClick={() => setOpen(true)}>
          パスワード変更
        </Button>
        <Button sx={BUTTON_SX} onClick={logout}>
          ログアウト
        </Button>
      </Stack>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>パスワード変更</DialogTitle>
        <DialogContent>
          {done ? (
            <Alert severity="success">
              パスワードを変更しました。他の端末はログアウトされています。
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ fontSize: 13 }}>
                仮パスワードを受け取った方は、ここでご自身のパスワードに変更してください。
                変更すると、この端末以外はログアウトされます。
              </DialogContentText>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="現在のパスワード"
                  name="current"
                  type="password"
                  value={form.current}
                  onChange={handleChange}
                  autoComplete="current-password"
                  fullWidth
                />
                <TextField
                  label={`新しいパスワード（${MIN_PASSWORD_LENGTH}文字以上）`}
                  name="next"
                  type="password"
                  value={form.next}
                  onChange={handleChange}
                  autoComplete="new-password"
                  fullWidth
                />
                <TextField
                  label="新しいパスワード（確認）"
                  name="confirm"
                  type="password"
                  value={form.confirm}
                  onChange={handleChange}
                  autoComplete="new-password"
                  fullWidth
                />
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{done ? '閉じる' : 'キャンセル'}</Button>
          {!done && (
            <Button onClick={handleSubmit} disabled={busy} variant="contained">
              変更する
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AccountMenu;
